import { Helmet } from 'react-helmet-async';
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import { LogIn, UserPlus, AlertCircle } from 'lucide-react';

const LoginPage = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const navigate = useNavigate();

    const [isResetMode, setIsResetMode] = useState(false);
    const [resetSuccess, setResetSuccess] = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password,
            });

            if (error) throw error;

            navigate('/profil'); // Redirect to profile on success
        } catch (err) {
            setError(err.message || "Une erreur est survenue lors de la connexion.");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: window.location.origin + '/reset-password', // Redirect to dedicated reset page
            });

            if (error) throw error;
            setResetSuccess(true);
        } catch (err) {
            console.error("Auth Error:", err);
            const errMsg = err.message.toLowerCase();
            if (errMsg.includes('rate limit')) {
                setError("Trop de tentatives. Veuillez attendre un moment avant de réessayer.");
            } else if (errMsg.includes('security purposes')) {
                setError("Blocage de sécurité. Essayez une autre adresse email ou attendez quelques minutes.");
            } else if (errMsg.includes('error sending')) {
                setError("Erreur d'envoi d'email. Vérifiez que votre adresse est valide ou contactez le support.");
            } else {
                setError(err.message || "Impossible d'envoyer l'email de réinitialisation.");
            }
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-[#F0F9FF] flex flex-col items-center justify-center p-6 font-inter">
            <Helmet><meta name='robots' content='noindex, nofollow' /></Helmet>
            <div className="w-full max-w-md bg-white rounded-[32px] shadow-xl border border-blue-50 p-8 md:p-10 relative overflow-hidden">
                {/* Decorative Elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-blue-50 rounded-bl-[100px] -z-0 opacity-50" />
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-blue-100 rounded-tr-[80px] -z-0 opacity-30" />

                <div className="relative z-10">
                    <div className="text-center mb-10">
                        <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-lg shadow-blue-200 rotate-3">
                            <LogIn className="text-white w-8 h-8" />
                        </div>
                        <h1 className="text-3xl font-black text-slate-900 mb-2">
                            {isResetMode ? 'Mot de passe oublié ?' : 'Bienvenue !'}
                        </h1>
                        <p className="text-slate-500 font-medium">
                            {isResetMode
                                ? "Entrez votre email pour recevoir un lien de réinitialisation."
                                : "Connectez-vous à votre compte TrouvetonVIE"
                            }
                        </p>
                    </div>

                    {error && (
                        <div className="mb-6 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm font-bold flex items-center gap-2 animate-pulse">
                            <AlertCircle size={16} />
                            {error}
                        </div>
                    )}

                    {resetSuccess ? (
                        <div className="text-center">
                            <div className="mb-6 bg-green-50 border border-green-100 text-green-700 px-6 py-4 rounded-xl text-sm font-bold">
                                Un email de réinitialisation a été envoyé à <span className="text-blue-600">{email}</span>.
                                Vérifiez vos spams !
                            </div>
                            <button
                                onClick={() => { setIsResetMode(false); setResetSuccess(false); }}
                                className="text-blue-600 font-bold hover:underline"
                            >
                                Retour à la connexion
                            </button>
                        </div>
                    ) : (
                        <form onSubmit={isResetMode ? handleResetPassword : handleLogin} className="space-y-5">
                            <div className="space-y-1">
                                <label className="text-xs font-bold text-slate-400 uppercase tracking-wider ml-1">Email</label>
                                <input
                                    type="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder:font-medium placeholder:text-slate-400"
                                    placeholder="exemple@email.com"
                                    required
                                />
                            </div>

                            {!isResetMode && (
                                <div className="space-y-1">
                                    <div className="flex justify-between items-center ml-1">
                                        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Mot de passe</label>
                                        <button
                                            type="button"
                                            onClick={() => setIsResetMode(true)}
                                            className="text-xs font-bold text-blue-600 hover:text-blue-700 hover:underline"
                                        >
                                            Oublié ?
                                        </button>
                                    </div>
                                    <input
                                        type="password"
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        className="w-full px-5 py-3.5 bg-slate-50 border border-slate-200 rounded-xl font-bold text-slate-700 outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-400 transition-all placeholder:font-medium placeholder:text-slate-400"
                                        placeholder="••••••••"
                                        required
                                    />
                                </div>
                            )}

                            <div className="pt-2">
                                <button
                                    type="submit"
                                    disabled={loading}
                                    className="w-full bg-blue-600 text-white font-bold py-4 rounded-xl shadow-xl shadow-blue-200 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transform active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                                >
                                    {loading ? (
                                        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                                    ) : (
                                        <>
                                            {isResetMode ? 'Envoyer le lien' : 'Se connecter'}
                                        </>
                                    )}
                                </button>
                            </div>
                        </form>
                    )}

                    {!resetSuccess && (
                        <div className="mt-8 pt-8 border-t border-slate-100 text-center">
                            {isResetMode ? (
                                <button
                                    onClick={() => setIsResetMode(false)}
                                    className="text-slate-500 font-bold text-sm hover:text-slate-700"
                                >
                                    Annuler et revenir à la connexion
                                </button>
                            ) : (
                                <>
                                    <p className="text-slate-500 font-medium text-sm mb-4">Pas encore de compte ?</p>
                                    <Link to="/onboarding" className="inline-flex items-center gap-2 text-blue-600 font-black text-sm hover:underline group">
                                        <UserPlus size={16} className="group-hover:scale-110 transition-transform" />
                                        Créer un compte gratuitement
                                    </Link>
                                </>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LoginPage;
